import React, { Fragment, useMemo, useState } from 'react';

import classNames from 'classnames';

import ClampTextWithReadMore from '@store:web/components/ClampTextWithReadMore';

import { ComponentWrapperElementType } from '../../../utils/types';

interface TabProps {
  name: string;
  content: string;
}

interface TabsProps<T extends TabProps> {
  tabs: T[];
  defaultTab?: T['name'];
  className?: string;
  TabWrapper?: ComponentWrapperElementType;
}

const Tabs = <T extends TabProps>({
  tabs,
  TabWrapper = Fragment,
  defaultTab,
  className = '',
}: TabsProps<T>) => {
  const defaultActiveTab = useMemo(() => {
    if (defaultTab) {
      return defaultTab;
    }

    const firstTabWithContent = tabs.filter((tab) => Boolean(tab.content));
    return firstTabWithContent[0]?.name || '';
  }, [defaultTab, tabs]);
  const [activeTab, setActiveTab] = useState<T['name']>(defaultActiveTab);
  const currentContent = useMemo(() => {
    const tabActive = tabs.find((tab) => tab.name === activeTab);
    return tabActive ? tabActive.content : '';
  }, [activeTab, tabs]);

  return (
    <div className={classNames(className)}>
      <div className="flex flex-row gap-10 mb-6 overflow-auto hide-scrollbar border-b border-neutral-60">
        {tabs.map((tab) => {
          if (!tab.content) {
            return null;
          }

          return (
            <TabWrapper key={tab.name}>
              <button
                className={classNames('pb-6 font-semibold text-nowrap', {
                  'border-b border-neutral-700': activeTab === tab.name,
                })}
                onClick={() => setActiveTab(tab.name)}
              >
                {tab.name}
              </button>
            </TabWrapper>
          );
        })}
      </div>

      <div>
        <ClampTextWithReadMore
          className="inner-html text-sm pb-8 lg:pb-0"
          text={currentContent}
          maxLines={4}
          key={activeTab}
        />
      </div>
    </div>
  );
};

export default Tabs;
